import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { makeStyles } from '@mui/styles';
import _ from 'lodash';
import {
  Button,
  Container,
  Divider as MuiDivider,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow as TableRowMui,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import styled from 'styled-components';
import {
  useAllNotificationRules,
  useRuleTriggers,
  useRuleTriggerOptions,
  useAllUsers,
  useNewNotificationRule,
  useNewNotificationRecipient,
  useUpdateNotificationRule,
  useDeleteNotificationRule,
  useNewNotificationTemplate,
  useUpdateNotificationTemplate,
} from 'shared/apollo';
import { QuickDrawer, ConfirmationDialog } from 'shared/components';
import SelectTrigger from './SelectTrigger';
import EmailTemplate from './EmailTemplate';
import TriggerOptions from './TriggerOptions';
import DeliveryOptions from './DeliveryOptions';
import Recipients from './Recipients';
import Timing from './Timing';
import EditIcon from '@mui/icons-material/Edit';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import MultiFilter from './MultiFilter';
import EditIconSvg from 'shared/statics/svg/Edit.svg';
import DeleteSvg from 'shared/statics/svg/Delete.svg';
import { useUser } from 'shared/auth';

const initialValues = {
  id: null,
  defaultTemplate: true,
  customTemplate: false,
  templateId: null,
  notificationSubject: '',
  notificationBody: '',
  name: '',
  ruleTrigger: '',
  ruleTriggerOptions: [],
  deliveryImmediate: true,
  deliveryDigest: false,
  deliveryDigestAt: null,
  deliveryDigestWeekday: null,
  recipients: [],
  timingList: [],
  recipientIncludeNpo: false,
  recipientIncludeProgramManager: false,
  recipientIncludeAllNpoUsers: false,
};

const useStyles = makeStyles(() => ({
  root: {
    margin: 'auto',
  },
  container: {
    backgroundColor: '#f9f9f9f9',
    paddingLeft: '0px',
    paddingRight: '0px',
  },
  leftButton: {
    marginRight: '5px',
  },
}));

function Row({
  row,
  onEditNotificationRule,
  handleOpenDeleteDialog,
  donorDefault,
}) {
  let displayRecipients = '';
  if (row.data.recipientIncludeProgramManager) {
    if (displayRecipients.length > 0) displayRecipients += ', ';
    displayRecipients += 'Program Manager';
  }
  if (row.data.recipientIncludeNpo) {
    if (displayRecipients.length > 0) displayRecipients += ', ';
    displayRecipients +=
      row.trigger === 'Invitation sent'
        ? 'NPO investment recipient'
        : 'NPO program manager';
  }
  if (row.data?.recipientIncludeAllNpoUsers) {
    if (displayRecipients.length > 0) displayRecipients += ', ';
    displayRecipients += 'All npo users';
  }
  if (row.recipients) {
    if (displayRecipients.length > 0) displayRecipients += ', ';
    displayRecipients += row.recipients;
  }
  if (displayRecipients.length === 0) {
    displayRecipients = '-';
  }

  const onDelete = () => {
    if (!donorDefault) {
      handleOpenDeleteDialog();
    }
  };

  return (
    <React.Fragment>
      <TableRow>
        <TableCell onClick={onEditNotificationRule} align="left">
          <div>{row.name}</div>
        </TableCell>
        <TableCell onClick={onEditNotificationRule} align="left">
          {row.trigger}
        </TableCell>
        <TableCell onClick={onEditNotificationRule} align="left">
          {row.delivery}
        </TableCell>
        <TableCell onClick={onEditNotificationRule} align="left">
          <Tooltip title={displayRecipients} placement="bottom-end" arrow>
            <span>{displayRecipients}</span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={onEditNotificationRule}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={onDelete}
            className={donorDefault ? 'disabled' : ''}
          >
            <DeleteTwoToneIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    sent: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    trigger: PropTypes.string.isRequired,
    portfolio: PropTypes.string.isRequired,
    npoProgram: PropTypes.string.isRequired,
    delivery: PropTypes.string.isRequired,
    recipients: PropTypes.string.isRequired,
    history: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number.isRequired,
        customerId: PropTypes.string.isRequired,
        date: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
};

const getFilterStr = values => {
  if (values[0] === 'All') return 'All';
  return values.filter(t => t !== 'All').join(', ');
};

const FilterGroup = ({
  history,
  showBackButton,
  setFilterPortfolio,
  setFilterNpo,
  setFilterProgram,
  totalRow,
  handleAddNewNotification,
  triggers,
  filterTrigger,
  setFilterTrigger,
  recipients,
  filterRecipient,
  setFilterRecipient,
  names,
  setFilterName,
  filterName,
  deliveries,
  setFilterDelivery,
  filterDelivery,
}) => {
  const clearAll = event => {
    event.preventDefault();
    setFilterPortfolio();
    setFilterNpo();
    setFilterProgram();
    setFilterTrigger();
    setFilterRecipient();
    setFilterDelivery();
    setFilterName();
  };

  const viewLog = () => {
    history.push('/account/notifications_logs');
  };

  return (
    <Heading>
      <RowH>
        <ColH>
          <h2>
            {showBackButton && (
              <span>
                <ArrowBackIcon onClick={viewLog} />
              </span>
            )}
            Notification Rules <label>({totalRow})</label>
          </h2>
        </ColH>
        <Button onClick={handleAddNewNotification}>Add New Rule</Button>
      </RowH>
      <RowH>
        <ColH>
          <span>Filter By:</span>
        </ColH>
        <LinkClear onClick={clearAll}>Clear filters</LinkClear>
      </RowH>
      <RowH>
        <ColH className="filters">
          <MultiFilter
            options={names}
            value={filterName}
            onChange={setFilterName}
            placeholder="Name"
          />
        </ColH>
        <ColH className="filters">
          <MultiFilter
            options={triggers}
            value={filterTrigger}
            onChange={setFilterTrigger}
            placeholder="Trigger"
          />
        </ColH>
        <ColH className="filters">
          <MultiFilter
            options={deliveries}
            value={filterDelivery}
            onChange={setFilterDelivery}
            placeholder="Delivery"
          />
        </ColH>
        <ColH className="filters">
          <MultiFilter
            options={recipients}
            value={filterRecipient}
            onChange={setFilterRecipient}
            placeholder="Recipient(s)"
          />
        </ColH>
      </RowH>
      <RowH>
        {filterName && filterName.length > 0 && (
          <Chip
            label={`Name: ${getFilterStr(filterName)}`}
            onDelete={() => setFilterName()}
          />
        )}
        {filterTrigger && filterTrigger.length > 0 && (
          <Chip
            label={`Trigger: ${getFilterStr(filterTrigger)}`}
            onDelete={() => setFilterTrigger()}
          />
        )}
        {filterDelivery && filterDelivery.length > 0 && (
          <Chip
            label={`Delivery: ${getFilterStr(filterDelivery)}`}
            onDelete={() => setFilterDelivery()}
          />
        )}
        {filterRecipient && filterRecipient.length > 0 && (
          <Chip
            label={`Recipients: ${getFilterStr(filterRecipient)}`}
            onDelete={() => setFilterRecipient()}
          />
        )}
      </RowH>
    </Heading>
  );
};

const ALLOWED_TRIGGERS_GRANTEE = [1, 3, 4, 5, 6, 7, 14];

export default function Notifications({ history }) {
  const classes = useStyles();
  const { data: rows, isLoading, refresh } = useAllNotificationRules();
  const { run: runNewRule, isLoading: loadingNew } = useNewNotificationRule();
  const { run: runUpdateRule } = useUpdateNotificationRule();
  const { run: runDeleteRule } = useDeleteNotificationRule();
  const { run: runNewRecipient } = useNewNotificationRecipient();
  const { data: users } = useAllUsers();
  const { data: ruleTriggers } = useRuleTriggers();
  const { data: ruleTriggerOptions } = useRuleTriggerOptions();
  const { run: runNewTemplate } = useNewNotificationTemplate();
  const { run: runUpdateTemplate } = useUpdateNotificationTemplate();
  const user = useUser();

  const rulesByRole =
    user?.role === 'grantee'
      ? (ruleTriggers || []).filter(
          trigger => ALLOWED_TRIGGERS_GRANTEE.indexOf(trigger.id) > -1,
        )
      : ruleTriggers;

  const [open, setOpen] = useState(false);
  const [filterTrigger, setFilterTrigger] = useState();
  const [filterPortfolio, setFilterPortfolio] = useState();
  const [filterNpo, setFilterNpo] = useState();
  const [filterProgram, setFilterProgram] = useState();
  const [filterRecipient, setFilterRecipient] = useState();
  const [filterName, setFilterName] = useState();
  const [filterDelivery, setFilterDelivery] = useState();
  const [ruleName, setRuleName] = useState();
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [ruleToBeDeleted, setRuleToBeDeleted] = useState();
  const [urlLoaded, setUrlLoaded] = useState(false);
  const [discardDialog, setDiscardDialog] = useState(false);
  const [formInitial, setFormInitial] = useState(initialValues);
  const [sortConf, setSortConf] = useState({
    activeField: 'name',
    name: 'asc',
    trigger: 'asc',
    delivery: 'asc',
    recipient: 'asc',
  });

  const getIsTimed = triggerId => {
    return [5, 10, 11, 12, 13].indexOf(triggerId) > -1;
  };

  const form = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues,
    onSubmit: (value, { resetForm }) => {
      const timed = getIsTimed(value.ruleTrigger);
      setOpen(false);
      value.ruleTriggerOptions = JSON.stringify(value.ruleTriggerOptions);
      if (timed) {
        value.timingList = JSON.stringify(
          value.timingList.map(item => ({
            ...item,
            timing: item.timing.replace(' ', ''),
          })),
        );
      } else {
        value.timingList = '[]';
      }
      if (value.id) {
        if (value.deliveryDigestAt === '') value.deliveryDigestAt = null;
        if (value.deliveryDigestWeekday === '')
          value.deliveryDigestWeekday = null;
        handleEdit(value, resetForm);
      } else {
        handleNew(value, resetForm);
      }
    },
    validate: values => {
      const errors = {};
      const timed = getIsTimed(values.ruleTrigger);
      if (!values.name) {
        errors.name = 'Write a name';
      }
      if (!values.ruleTrigger) {
        errors.ruleTrigger = 'Select a trigger';
      }
      if (timed && values.timingList.length === 0) {
        errors.timingList = 'At least one timing is required';
      }
      return errors;
    },
  });

  const isTimed = getIsTimed(form.values.ruleTrigger);
  const availableRuleTriggerOptions = ruleTriggerOptions.filter(
    option => option.ruleTrigger === form.values.ruleTrigger,
  );

  const addRecipients = (ruleId, recipients, resetForm) => {
    if (recipients && recipients.length > 0) {
      recipients.forEach((recipient, i) => {
        runNewRecipient(
          {
            rule: ruleId,
            userId: recipient.id,
          },
          () => {
            if (i + 1 === recipients.length) {
              refresh();
              resetForm({ values: '' });
            }
          },
        );
      });
    } else {
      refresh();
      resetForm({ values: '' });
    }
  };

  const addOrUpdateTemplate = useCallback(
    (name, template, id = null, onDone) => {
      if (id) {
        runUpdateTemplate({ id, name, template }, onDone);
      } else {
        runNewTemplate({ name, template }, onDone);
      }
    },
    [runUpdateTemplate, runNewTemplate],
  );

  const handleNew = useCallback(
    (value, resetForm) => {
      const {
        recipients,
        customTemplate,
        defaultTemplate,
        notificationBody,
        notificationSubject,
        templateId,
        ...form
      } = value;
      if (customTemplate && notificationBody && notificationSubject) {
        addOrUpdateTemplate(
          notificationSubject,
          notificationBody,
          templateId,
          templateId => {
            form.template = templateId;
            runNewRule(form, ruleId =>
              addRecipients(ruleId, recipients, resetForm),
            );
          },
        );
      } else {
        form.template = null;
        runNewRule(form, ruleId =>
          addRecipients(ruleId, recipients, resetForm),
        );
      }
    },
    [addOrUpdateTemplate, runNewRule, addRecipients],
  );

  const handleEdit = (value, resetForm) => {
    const {
      recipients,
      customTemplate,
      defaultTemplate,
      notificationBody,
      notificationSubject,
      templateId,
      ...form
    } = value;
    if (value.deliveryDigestAt === '') value.deliveryDigestAt = null;
    if (value.deliveryDigestWeekday === '') value.deliveryDigestWeekday = null;
    if (customTemplate && notificationBody && notificationSubject) {
      // add or update template and wait for ID
      addOrUpdateTemplate(
        notificationSubject,
        notificationBody,
        templateId,
        templateId => {
          form.template = templateId;
          runUpdateRule(form, ruleId =>
            addRecipients(ruleId, recipients, resetForm),
          );
        },
      );
    } else {
      form.template = null;
      runUpdateRule(form, ruleId =>
        addRecipients(ruleId, recipients, resetForm),
      );
    }
  };

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    const fInit = JSON.stringify(formInitial);
    const fCurr = JSON.stringify(form.values);
    if (fInit === fCurr) {
      setOpen(false);
    } else {
      setDiscardDialog(true);
    }
  };

  const proceedAndDiscard = () => {
    setDiscardDialog(false);
    setOpen(false);
  };

  const capitalize = s => {
    if (typeof s !== 'string') return '';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const toTitleCase = str => {
    return str.replace('_', ' ').replace(/\w\S*/g, function(txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  // eslint-disable-next-line
  const handleEditNotificationRule = useCallback(rule => {
    const {
      dbId,
      deliveryDigest,
      deliveryDigestAt,
      deliveryDigestWeekday,
      deliveryImmediate,
      name,
      recipients,
      ruleTrigger,
      ruleTriggerOptions: rto,
      recipientIncludeNpo,
      recipientIncludeProgramManager,
      recipientIncludeAllNpoUsers,
      timing,
      getTemplate: template,
      donorDefault,
    } = rule.data;

    const ruleTriggerOptions = rto && rto.length > 0 ? JSON.parse(rto) : [];
    const timingList = timing.map(row => ({
      days: row.days,
      timing: toTitleCase(row.mode),
    }));
    const recipientList = recipients.map(rcp => ({
      id: rcp.userId,
      name: rcp.fullName,
    }));
    form.setFieldValue('id', dbId);
    form.setFieldValue('name', name);
    form.setFieldValue('ruleTrigger', ruleTrigger);
    form.setFieldValue('deliveryDigest', deliveryDigest);
    form.setFieldValue('deliveryImmediate', deliveryImmediate);
    form.setFieldValue('recipientIncludeNpo', recipientIncludeNpo);
    form.setFieldValue('deliveryDigestAt', capitalize(deliveryDigestAt));
    form.setFieldValue(
      'deliveryDigestWeekday',
      capitalize(deliveryDigestWeekday),
    );
    form.setFieldValue('ruleTriggerOptions', ruleTriggerOptions);
    form.setFieldValue(
      'recipientIncludeProgramManager',
      recipientIncludeProgramManager,
    );
    form.setFieldValue(
      'recipientIncludeAllNpoUsers',
      recipientIncludeAllNpoUsers,
    );
    form.setFieldValue('recipients', recipientList);
    form.setFieldValue('timingList', timingList);
    form.setFieldValue('donorDefault', donorDefault);
    const initialForm = {
      id: dbId,
      name,
      ruleTrigger,
      ruleTriggerOptions,
      deliveryImmediate,
      deliveryDigest,
      deliveryDigestAt: capitalize(deliveryDigestAt),
      deliveryDigestWeekday: capitalize(deliveryDigestWeekday),
      recipients: recipientList,
      timingList,
      recipientIncludeNpo,
      recipientIncludeProgramManager,
      recipientIncludeAllNpoUsers,
    };
    // has a custom template
    if (template?.dbId) {
      form.setFieldValue('defaultTemplate', false);
      form.setFieldValue('customTemplate', true);
      form.setFieldValue('templateId', template?.dbId);
      form.setFieldValue('notificationSubject', template?.name);
      form.setFieldValue('notificationBody', template?.template);
      initialForm.templateId = template?.dbId;
      initialForm.notificationSubject = template?.name;
      initialForm.notificationBody = template?.template;
    } else {
      form.setFieldValue('defaultTemplate', true);
      form.setFieldValue('customTemplate', false);
      form.setFieldValue('templateId', undefined);
      form.setFieldValue('notificationSubject', undefined);
      form.setFieldValue('notificationBody', undefined);
      initialForm.templateId = undefined;
      initialForm.notificationSubject = undefined;
      initialForm.notificationBody = undefined;
    }
    setFormInitial(initialForm);
    handleOpenModal();
  });

  useEffect(() => {
    if (
      !urlLoaded &&
      rows &&
      rows.length > 0 &&
      history &&
      history.location &&
      history.location.search
    ) {
      setUrlLoaded(true);
      const ruleId = new URLSearchParams(history.location.search).get('id');
      if (ruleId) {
        const rule = rows.filter(
          row => row.data.dbId === parseInt(ruleId),
        )?.[0];
        if (rule) {
          handleEditNotificationRule(rule);
        }
      }
    }
  }, [handleEditNotificationRule, history, rows, urlLoaded]);

  const handleAddNewNotification = () => {
    form.setFieldValue('id', null);
    form.setFieldValue('name', '');
    form.setFieldValue('ruleTrigger', '');
    form.setFieldValue('deliveryDigest', false);
    form.setFieldValue('deliveryImmediate', true);
    form.setFieldValue('recipientIncludeNpo', false);
    form.setFieldValue('deliveryDigestAt', null);
    form.setFieldValue('ruleTriggerOptions', []);
    form.setFieldValue('deliveryDigestWeekday', null);
    form.setFieldValue('recipientIncludeProgramManager', false);
    form.setFieldValue('recipientIncludeAllNpoUsers', false);
    form.setFieldValue('recipients', []);
    form.setFieldValue('timingList', []);
    setFormInitial(initialValues);
    handleOpenModal();
  };

  const handleDelete = () => {
    setDeleteDialog(false);
    setOpen(false);
    runDeleteRule({ id: ruleToBeDeleted }, () => {
      refresh();
    });
  };

  const handleOpenDeleteDialog = (itemId, name) => {
    setDeleteDialog(true);
    setRuleToBeDeleted(itemId);
    setRuleName(name);
  };

  const filteredRows = useMemo(() => {
    let data = _.cloneDeep(rows);
    if (filterTrigger && filterTrigger.length > 0) {
      data = data.filter(item => filterTrigger.indexOf(item.trigger) > -1);
    }
    if (filterRecipient && filterRecipient.length > 0) {
      data = data.filter(item => {
        return filterRecipient.reduce((acc, r) => {
          if (item.recipients.includes(r)) acc = true;
          return acc;
        }, false);
      });
    }
    if (filterName && filterName.length > 0) {
      data = data.filter(item => filterName.indexOf(item.name) > -1);
    }
    if (filterDelivery && filterDelivery.length > 0) {
      data = data.filter(item => filterDelivery.indexOf(item.delivery) > -1);
    }
    /* SORTING */
    if (sortConf.activeField === 'name') {
      return _.orderBy(data, row => row.name.toLowerCase(), sortConf.name);
    }
    if (sortConf.activeField === 'trigger') {
      return _.orderBy(data, row => row.trigger, sortConf.trigger);
    }
    if (sortConf.activeField === 'delivery') {
      return _.orderBy(data, row => row.delivery, sortConf.delivery);
    }
    if (sortConf.activeField === 'recipient') {
      return _.orderBy(
        data,
        row => row.recipients.toLowerCase(),
        sortConf.recipient,
      );
    }
  }, [
    rows,
    filterTrigger,
    filterRecipient,
    filterName,
    filterDelivery,
    sortConf,
  ]);

  const availablePortfolios = _.uniqBy(
    rows.filter(r => r.portfolio !== '-').map(r => r.portfolio),
  );
  availablePortfolios.unshift('Portfolio');
  const availableNpos = _.uniqBy(
    rows.filter(r => r.npoProgram !== '-').map(r => r.npoProgram),
  );
  availableNpos.unshift('Npo');
  const availablePrograms = _.uniqBy(
    rows.filter(r => r.program !== '-').map(r => r.program),
  );
  availablePrograms.unshift('Program');
  const availableTrigger = _.uniqBy(
    rows.filter(r => r.trigger !== '-').map(r => r.trigger),
  );
  availableTrigger.unshift('All');
  const availableRecipients = useMemo(() => {
    const recipients = rows.reduce((acc, row) => {
      if (row && row.data && row.data.recipients) {
        row.data.recipients.forEach(recip => {
          if (acc.indexOf(recip.fullName) === -1) acc.push(recip.fullName);
        });
      }
      return acc;
    }, []);
    recipients.unshift('All');
    return recipients;
  }, [rows]);
  const availableNames = _.uniqBy(
    rows.filter(r => r.name !== '-').map(r => r.name),
  );
  availableNames.unshift('All');
  const availableDeliveries = _.uniqBy(
    rows.filter(r => r.delivery !== '-').map(r => r.delivery),
  );
  availableDeliveries.unshift('All');

  const onPressAr = activeField => {
    const copyConf = { ...sortConf, activeField };
    const ascDesc = copyConf[activeField] === 'asc' ? 'desc' : 'asc';
    copyConf[activeField] = ascDesc;
    setSortConf(copyConf);
  };

  const handleUpdateEmailTemplate = useCallback(
    (subject, body) => {
      if (subject && body) {
        const templateId = form?.values?.templateId;
        addOrUpdateTemplate(subject, body, templateId, () => { /* noop */ });
      }
    },
    [form?.values?.templateId, addOrUpdateTemplate],
  );

  const sortArrowName =
    sortConf.name === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;
  const sortArrowTrigger =
    sortConf.trigger === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;
  const sortArrowDelivery =
    sortConf.delivery === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;
  const sortArrowRecipient =
    sortConf.recipient === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;

  return (
    <Container maxWidth={false} className={classes.container}>
      <Grid
        container
        spacing={2}
        justify="center"
        alignItems="center"
        className={classes.root}
      >
        <FilterGroup
          showBackButton={false}
          history={history}
          portfolios={availablePortfolios}
          setFilterPortfolio={setFilterPortfolio}
          npos={availableNpos}
          setFilterNpo={setFilterNpo}
          programs={availablePrograms}
          setFilterProgram={setFilterProgram}
          filterPortfolio={filterPortfolio}
          filterNpo={filterNpo}
          filterProgram={filterProgram}
          totalRow={filteredRows.length}
          handleAddNewNotification={handleAddNewNotification}
          triggers={availableTrigger}
          filterTrigger={filterTrigger}
          setFilterTrigger={setFilterTrigger}
          recipients={availableRecipients}
          filterRecipient={filterRecipient}
          setFilterRecipient={setFilterRecipient}
          names={availableNames}
          filterName={filterName}
          setFilterName={setFilterName}
          deliveries={availableDeliveries}
          filterDelivery={filterDelivery}
          setFilterDelivery={setFilterDelivery}
        />
        {(isLoading || loadingNew) && (
          <Grid item xs={12}>
            <LinearProgress variant="query" />
          </Grid>
        )}
        <Grid
          container
          spacing={2}
          justify="center"
          alignItems="center"
          className={classes.root}
        >
          {!isLoading && rows.length > 0 && (
            <Grid item xs={12}>
              <TableRoot>
                <Table aria-label="collapsible table">
                  <TableHead>
                    <TableRow>
                      <TableCell onClick={() => onPressAr('name')} align="left">
                        Name {sortArrowName}
                      </TableCell>
                      <TableCell
                        onClick={() => onPressAr('trigger')}
                        align="left"
                      >
                        Trigger {sortArrowTrigger}
                      </TableCell>
                      {/*<TableCell align="left">Recent Portfolio Match</TableCell>*/}
                      {/*<TableCell align="left">Recent NPO/Program Match</TableCell>*/}
                      <TableCell
                        onClick={() => onPressAr('delivery')}
                        align="left"
                      >
                        Delivery {sortArrowDelivery}
                      </TableCell>
                      <TableCell
                        onClick={() => onPressAr('recipient')}
                        align="left"
                      >
                        Recipient(s) {sortArrowRecipient}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row, idx) => (
                      <Row
                        donorDefault={row.donorDefault}
                        key={idx}
                        row={row}
                        onEditNotificationRule={() =>
                          handleEditNotificationRule(row)
                        }
                        handleOpenDeleteDialog={() =>
                          handleOpenDeleteDialog(row.data.dbId, row.name)
                        }
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableRoot>
            </Grid>
          )}
          {!isLoading && rows.length === 0 && (
            <Grid item xs={12} className={classes.empty}>
              <Typography component="subtitle2" variant="subtitle2" noWrap>
                {'There are no notifications rules yet...'}
              </Typography>
            </Grid>
          )}
          {!isLoading && rows.length > 0 && filteredRows.length === 0 && (
            <Grid item xs={12} className={classes.empty}>
              <Typography component="subtitle2" variant="subtitle2" noWrap>
                {'There are no rules matching your filters...'}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
      <QuickDrawer
        open={open}
        onClose={handleCloseModal}
        width={'432px'}
        title=""
      >
        <DrawerHeader>
          <p>{form.values.id ? 'Edit' : 'New'} Notification Rule</p>
        </DrawerHeader>
        <Scroller>
          <Content>
            <SelectTrigger
              donorDefault={form?.values?.donorDefault}
              form={form}
              ruleTriggers={rulesByRole}
            />
            <Divider />
            <EmailTemplate
              donorDefault={form?.values?.donorDefault}
              form={form}
              ruleTriggerOptions={ruleTriggerOptions}
              handleUpdate={handleUpdateEmailTemplate}
            />
            <Divider />
            <TriggerOptions
              donorDefault={form?.values?.donorDefault}
              form={form}
              options={availableRuleTriggerOptions}
            />
            <Divider />
            <DeliveryOptions
              donorDefault={form?.values?.donorDefault}
              form={form}
            />
            <Divider />
            <Recipients
              donorDefault={form?.values?.donorDefault}
              form={form}
              users={users}
              isTimed={isTimed}
            />
            <Divider />
            {isTimed && <Timing form={form} />}
            <Grid item xs={12} className="lowerButtons">
              <Button onClick={form.handleSubmit}>Save</Button>
              <Button
                disabled={!form.values.id}
                onClick={() =>
                  handleOpenDeleteDialog(form.values.id, form.values.name)
                }
              >
                Delete Rule
              </Button>
              <Button className={classes.leftButton} onClick={handleCloseModal}>
                Cancel
              </Button>
            </Grid>
          </Content>
        </Scroller>
      </QuickDrawer>
      <ConfirmationDialog
        header={'Delete Notification Rule'}
        openDialog={deleteDialog}
        onCloseDialog={() => setDeleteDialog(false)}
        actionButtonOne={handleDelete}
        actionButtonTwo={() => setDeleteDialog(false)}
        textButtonOne="Delete"
        textButtonTwo="Cancel"
        title={`Delete Notification ${ruleName}? This action is permanent.`}
      />
      <ConfirmationDialog
        header={'Notification Rule'}
        openDialog={discardDialog}
        onCloseDialog={() => setDiscardDialog(false)}
        actionButtonOne={proceedAndDiscard}
        actionButtonTwo={() => setDiscardDialog(false)}
        textButtonOne="Discard"
        textButtonTwo="Cancel"
        title={'Discard unsaved changes?'}
      />
    </Container>
  );
}

const Content = styled.div`
  width: 100%;
  padding: 20px;
  padding-top: 0px;
  .MuiInputBase-root {
    border-radius: 4px;
    &.Mui-disabled {
      background: #f1f1f1 !important;
    }
    .MuiInputBase-input {
      padding-top: 16px;
      padding-bottom: 16px;
      color: #48596b;
      font-size: 16px;
      font-weight: 400;
      fieldset {
        border: solid 1px #cbd6e2;
      }
    }
  }
  p {
    color: #48596b;
    font-size: 16px;
    font-weight: 700;
    margin-top: 16px;
  }
  hr {
    border-color: #cbd6e2;
    margin-top: 16px;
    margin-bottom: 16px;
  }
  .triggers label.MuiFormControlLabel-root {
    height: 30px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    margin-left: 4px !important;
  }
  .MuiIconButton-root {
    color: #eaf0f6;
  }
  .flexGrid {
    display: flex;
    button {
      margin-top: 8px;
      margin-bottom: 4px;
      margin-left: 16px;
      border: solid 1px #115a79;
      text-transform: none !important;
      font-weight: 700;
      font-size: 16px;
    }
  }
`;

const Scroller = styled.div`
  overflow-y: scroll;
  height: calc(100vh - 140px);
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  .lowerButtons {
    display: flex;
    button {
      flex: 1;
      text-transform: none;
      height: 48px;
      color: #ffffff;
      font-size: 16px;
      font-weight: 700;
      &:first-of-type {
        margin-right: 5px;
      }
      &:last-of-type {
        margin-left: 5px;
      }
    }
  }
`;

const DrawerHeader = styled.div`
  width: 100%;
  height: auto;
  background-color: #eaf0f6;
  padding-bottom: 24px;
  padding-left: 24px;
  p {
    color: #48596b;
    font-size: 32px;
    font-weight: 400;
    margin-top: 0px;
    margin-bottom: 0px;
  }
`;

const TableRoot = styled(TableContainer)`
  table {
    border-spacing: 0px 8px;
    border-collapse: separate;
    padding-left: 24px;
    padding-right: 24px;
  }
  .MuiTableHead-root {
    tr {
      height: 16px !important;
      &:hover {
        background-color: transparent;
      }
      th {
        font-weight: 700;
        font-size: 16px;
        color: #48596b;
        border-right: solid 1px #cbd6e2;
        border-bottom: none;
        padding-top: 0px !important;
        padding-bottom: 0px !important;
        &:first-of-type {
          color: #0072a2;
        }
        svg {
          transform: translateY(7px);
        }
        &:nth-of-type(4),
        &:nth-of-type(5) {
          border-right: none;
        }
        &:nth-of-type(1) {
          width: 25%;
        }
        &:nth-of-type(2) {
          width: 25%;
        }
        &:nth-of-type(3) {
          width: 15%;
        }
        &:nth-of-type(4) {
          width: 25%;
        }
        &:nth-of-type(5) {
          width: 10%;
        }
      }
    }
  }
  .MuiTableBody-root {
    tr {
      background-color: #fff;
      &:hover {
        background-color: #eaf0f6;
        .MuiIconButton-label {
          opacity: 1;
        }
      }
      td {
        font-weight: 400;
        font-size: 16px;
        color: #48596b;
        border-bottom: solid 1px #cbd6e2;
        border-top: solid 1px #cbd6e2;
        padding-bottom: 16px;
        padding-top: 16px;
        max-width: 100px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        &:first-of-type {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
          border-left: solid 1px #cbd6e2;
        }
        &:last-of-type {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
          border-right: solid 1px #cbd6e2;
          text-align: right;
        }
        .MuiIconButton-label {
          opacity: 0;
          color: #0072a2;
        }
        .MuiButtonBase-root {
          .MuiIconButton-label {
            &:before {
              content: '';
              width: 20px;
              height: 19px;
              background-size: cover;
            }
            .MuiSvgIcon-root {
              display: none;
            }
          }
          &:first-of-type {
            .MuiIconButton-label {
              &:before {
                background-image: url(${EditIconSvg});
              }
            }
          }
          &:last-of-type {
            .MuiIconButton-label {
              &:before {
                background-image: url(${DeleteSvg});
              }
            }
          }
          &.disabled {
            .MuiIconButton-label {
              filter: grayscale(100%) !important;
              &:before {
                opacity: 0.5;
              }
            }
          }
        }
      }
    }
  }
`;

const TableRow = styled(TableRowMui)`
  cursor: pointer;
  &:hover {
    background-color: #0091ae1f;
  }
`;

const Divider = styled(MuiDivider)`
  margin-top: 20px;
  margin-bottom: 20px;
`;

const Heading = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: #ebf0f6;
  padding-left: 24px;
  padding-right: 24px;
  padding-top: 24px;
  div:first-child {
    position: relative;
  }
  span {
    font-size: 16px;
    font-weight: 700;
    color: #48596b;
  }
  h2 {
    font-size: 32px;
    font-weight: 400;
    color: #48596b;
    span {
      color: #0072a2;
      cursor: pointer;
    }
    label {
      font-weight: 700;
      font-size: 24px;
      color: #778ba5;
    }
  }
  .MuiButtonGroup-root {
    width: 100%;
    background-color: white;
    border: solid 1px #cbd6e2;
    padding: 5px;
    max-height: 48px;
    button {
      max-height: 34px;
      justify-content: left;
      span {
        font-size: 16px;
        font-weight: 400;
        color: #535353;
      }
      &:first-of-type {
        width: 100%;
        text-transform: none;
      }
      border: none;
    }
  }
  .options {
    width: 100%;
    transform: translate(0px, 46px);
    z-index: 1;
  }
`;

const RowH = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  margin-bottom: 17px;
  button {
    text-transform: none;
    height: 48px;
    span {
      color: #ffffff;
      font-size: 16px;
      font-weight: 700;
    }
  }
  .MuiChip-root {
    background-color: #48596b;
    height: 32px;
    margin-right: 10px;
    max-width: 320px;
    span {
      color: #ffffff;
      font-size: 16px;
      font-weight: 400;
    }
    svg {
      color: #ffffff;
    }
  }
`;

const ColH = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  &.filters {
    padding-right: 16px;
    max-width: 25%;
    position: relative;
  }
`;

const LinkClear = styled.span`
  font-size: 16px !important;
  font-weight: 400 !important;
  color: #0072a2 !important;
  text-decoration: underline;
  cursor: pointer;
`;
